import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HEADQUARTERS_NAME } from '@speeki/dictionary';
import { PaginatedSelectSelectFieldProps } from '@speeki/global-ui-components';

import {
    SubsidiarySitesDocument,
    SubsidiarySitesQuery,
    SubsidiarySitesQueryVariables,
} from '@graphql/generated/graphql';

import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import { ERROR_MESSAGES } from '@shared/notifyMessages';

export const useSubsidiarySitesSelect = (
    params: LazyQueryHookOptions<
        SubsidiarySitesQuery,
        SubsidiarySitesQueryVariables
    >,
) => {
    const { t } = useTranslation();

    const [noResultsText, setResultsText] = useState<string | undefined>(
        undefined,
    );

    const [getSubsidiarySites] = useLazyQuery(SubsidiarySitesDocument, {
        fetchPolicy: 'cache-and-network',
    });

    const handleError = () => {
        setResultsText(t(ERROR_MESSAGES.selects.fetch));

        return {
            hasMore: false,
            options: [],
        };
    };

    const loadOptions: PaginatedSelectSelectFieldProps['loadOptions'] = async (
        filter,
        _,
        additional,
    ) => {
        const offset = additional?.offset ? additional.offset + 10 : 1;

        try {
            const { data, error } = await getSubsidiarySites({
                ...params,
                variables: {
                    input: {
                        ...params.variables?.input,
                        nameFilter: filter,
                        paging: {
                            limit: 10,
                            offset,
                        },
                    },
                },
            });

            if (error) return handleError();

            const { nodes, pageInfo } = data?.subsidiarySites || {};

            const options =
                nodes
                    ?.filter((node) => node.isActive)
                    .map((node) => ({
                        label:
                            (node.isHeadquarters
                                ? node.reportingLabel
                                : node.name) || '',
                        value: node.uuid || HEADQUARTERS_NAME,
                    })) || [];

            setResultsText(undefined);

            return {
                additional: {
                    offset,
                },
                hasMore: !!pageInfo?.hasNextPage,
                options: options ?? [],
            };
        } catch {
            return handleError();
        }
    };

    return {
        debounceTimeout: 200,
        loadOptions,
        noResultsText,
    };
};
